import { type GraphIssue, getPorts, type PortDef, summarizeNode } from "@velizhanin-dev/bot-scenario"
import { type Edge, MarkerType, type Node } from "@xyflow/react"
import type { DraftNode, ScenarioDraft } from "../api/types"
import { EDGE_COLOR } from "./node-appearance"

export const TARGET_HANDLE_ID = "in"

export type BlockNodeData = {
	node: DraftNode
	summary: string
	ports: PortDef[]
	issues: GraphIssue[]
	/** Движок прошёл блок в последнем событии тест-чата. */
	active: boolean
	/** «Прошли / Застряли» у настоящего бота; нет: статистику не показываем. */
	stats?: NodeStat
}

export interface NodeStat {
	passed: number
	stuck: number
	/** Нажали кнопку: ключ это id выхода кнопки. */
	clicks: Record<string, number>
	/** Открыть список людей: кто прошёл блок или кто на нём застрял. Название блока для подписи. */
	openPeople?: (mode: "passed" | "stuck", label: string) => void
}

export type BlockNode = Node<BlockNodeData, "block">

/** Блоки и связи одного потока в форме xyflow. Удаляются блоки только из панели блока. */
export function toFlowElements(
	draft: ScenarioDraft,
	flowId: string,
	issuesByNode: Map<string, GraphIssue[]>,
	selectedNodeId: string | null,
	activeNodeIds: ReadonlySet<string> = new Set(),
	nodeStats?: ReadonlyMap<string, NodeStat>,
): { nodes: BlockNode[]; edges: Edge[] } {
	const flowNodes = draft.nodes.filter((node) => node.flowId === flowId)
	const nodeIds = new Set(flowNodes.map((node) => node.id))

	const nodes: BlockNode[] = flowNodes.map((node) => ({
		id: node.id,
		type: "block",
		position: { x: node.positionX, y: node.positionY },
		data: {
			node,
			summary: summarizeNode(node, draft.flows, draft.tags),
			ports: getPorts(node),
			issues: issuesByNode.get(node.id) ?? [],
			active: activeNodeIds.has(node.id),
			...(nodeStats ? { stats: nodeStats.get(node.id) ?? { passed: 0, stuck: 0, clicks: {} } } : {}),
		},
		selected: node.id === selectedNodeId,
		deletable: false,
	}))

	const edges: Edge[] = draft.edges
		.filter((edge) => nodeIds.has(edge.sourceNodeId) && nodeIds.has(edge.targetNodeId))
		.map((edge) => ({
			id: edge.id,
			source: edge.sourceNodeId,
			sourceHandle: edge.sourcePort,
			target: edge.targetNodeId,
			targetHandle: TARGET_HANDLE_ID,
			markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14, color: EDGE_COLOR },
		}))

	return { nodes, edges }
}
